import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  createPortalSession,
  getBillingStatus
} from "./api/OrganizationClient";
import {
  BillingStatus,
  UserInfo
} from "./model/backendTypes";
import StripePricingTableModal from "./StripePricingTableModal";

// Props für das Profil-Dropdown:
// - user: aktuell eingeloggter Benutzer (Name/Org/E-Mail)
type Props = {
  user: UserInfo | null;
};

const ENV = (key: string): string | undefined => {
  const runtime = (window as Window & { __ENV__?: Record<string, string> }).__ENV__?.[key];
  if (runtime && runtime.length > 0) return runtime;
  const build = process.env[key];
  return build && build.length > 0 ? build : undefined;
};

const DEV_MODE = process.env.NODE_ENV !== "production";

// Formatiert Backend-Zeitstempel in ein lesbares deutsches Datum/Uhrzeit-Format.
function formatDate(ts?: string | null): string {
  if (!ts) return "–";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function renewalLabel(billing: BillingStatus): string {
  if (billing.subscription_status === "canceled") return "Ended on";
  if (billing.cancel_at_period_end) return "Ends on";
  return "Renewal";
}

export default function OrganizationDropdown({ user}: Props) {
  // Referenz auf den Dropdown-Container:
  // wird für "Click außerhalb schließen" verwendet.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // UI-State des Menüs und Billing-Status.
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalMenuOpen, setPortalMenuOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | undefined>(undefined);
  const [pricingOpen, setPricingOpen] = useState(false);
  const isOwner = user?.role === "owner";

  // Lädt den aktuellen Billing-Status vom Backend.
  // Danach werden auch Dev-Selects auf die aktuellen Werte synchronisiert.
  const loadBilling = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await getBillingStatus();
      setBilling(status);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load billing status.");
    } finally {
      setLoading(false);
    }
  }, []);

  const positionDropdown = useCallback(() => {
    const root = rootRef.current;
    const dropdown = dropdownRef.current;
    if (!root || !dropdown) return;

    const anchorRect = root.getBoundingClientRect();
    const viewportPadding = 12;
    const gap = 8;
    const width = Math.min(300, window.innerWidth - viewportPadding * 2);

    let left = anchorRect.right - width;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - viewportPadding - width));

    const measuredHeight = dropdown.getBoundingClientRect().height || 320;
    const availableBelow = window.innerHeight - (anchorRect.bottom + gap) - viewportPadding;
    const availableAbove = anchorRect.top - gap - viewportPadding;
    const shouldOpenUpward = availableBelow < Math.min(measuredHeight, 260) && availableAbove > availableBelow;

    let top: number;
    let maxHeight: number;

    if (shouldOpenUpward) {
      maxHeight = Math.max(180, availableAbove);
      const preferredTop = anchorRect.top - gap - Math.min(measuredHeight, maxHeight);
      top = Math.max(viewportPadding, preferredTop);
    } else {
      top = anchorRect.bottom + gap;
      maxHeight = Math.max(180, availableBelow);
    }

    setDropdownStyle({
      position: "fixed",
      top,
      left,
      width,
      maxHeight,
      zIndex: 2000,
    });
  }, []);

  // Schließt das Dropdown:
  // - bei Klick außerhalb
  // - bei Escape-Taste
  useEffect(() => {
    if (!open) return;

    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node;
      if (!rootRef.current.contains(target)) {
        setOpen(false);
      }
    };

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStatusOpen(false);
      setPortalMenuOpen(false);
      setDropdownStyle(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    let frame = 0;
    const syncPosition = () => {
      cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(positionDropdown);
    };

    syncPosition();
    window.addEventListener("resize", syncPosition);
    window.addEventListener("scroll", syncPosition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", syncPosition);
      window.removeEventListener("scroll", syncPosition, true);
    };
  }, [open, positionDropdown]);

  useEffect(() => {
    if (!open) return;
    positionDropdown();
  }, [
    open,
    positionDropdown,
    statusOpen,
    portalMenuOpen,
    billing,
    loading,
    error,
    portalLoading,
  ]);

  const toggleStatus = async () => {
    if (statusOpen) {
      setStatusOpen(false);
      return;
    }
    setStatusOpen(true);
    if (!billing && !loading) {
      await loadBilling();
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      const { portal_url } = await createPortalSession();
      window.location.assign(portal_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to open billing management.");
    } finally {
      setPortalLoading(false);
    }
  };


  return (
    // Äußerer Wrapper, damit das absolute Dropdown relativ zum Profile-Button positioniert wird.
    <div className="profile-menu" ref={rootRef}>
      {/* Trigger-Button für Öffnen/Schließen des Menüs */}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        organization
      </button>

      {open && (
        // Inhalt des Dropdown-Menüs (User-Infos + Billing-Bereich).
        <div className="profile-menu__dropdown" role="menu" ref={dropdownRef} style={dropdownStyle}>
          {user && (
            // User-Block aus den Daten, die vom Parent übergeben werden.
            <div className="profile-menu__user">
              <div className="profile-menu__avatar">
                {(user.organization_name?.[0] ?? user.name?.[0] ?? "O").toUpperCase()}
              </div>
              <div className="profile-menu__user-info">
                <div className="profile-menu__org">{user.organization_name}</div>
                <div className="profile-menu__meta">
                  {user.name} · {user.email}
                </div>
              </div>
            </div>
          )}

          {/* Billing-Sektion */}
          <div className="profile-menu__section-title">Status</div>

          {/* Aktion: Billing-Status laden */}
          <div className="billing-panel__actions">
            <button onClick={toggleStatus} disabled={loading}>
              {loading ? "loading..." : `Subscription ${statusOpen ? "▴" : "▾"}`}
            </button>
          </div>

          {/* Fehleranzeige bei API-Problemen */}
          {error && <p className="billing-panel__error">{error}</p>}

          {statusOpen && billing && (
            <div className="billing-panel__grid profile-menu__billing-grid">
              <div className="billing-panel__row">
                <span>Price-Tier</span>
                <strong>{billing.organization_abo}</strong>
              </div>
              <div className="billing-panel__row">
                <span>Paid</span>
                <strong>{billing.is_paid ? "yes" : "no"}</strong>
              </div>
              <div className="billing-panel__row">
                <span>Status</span>
                <strong>{billing.subscription_status}</strong>
              </div>
              <div className="billing-panel__row">
                <span>{renewalLabel(billing)}</span>
                <strong>{formatDate(billing.current_period_end)}</strong>
              </div>
            </div>
          )}

          {isOwner && (
            <>
              <div className="profile-menu__section-title">Upgrade</div>
              <div className="billing-panel__actions">
                <button
                  type="button"
                  onClick={() => setPricingOpen(true)}
                  disabled={portalLoading}
                >
                  Upgrade
                </button>
              </div>
            </>
          )}

          {isOwner && (
            <>
              <div className="profile-menu__section-title">Manage</div>
              <div className="billing-panel__actions">
                <button onClick={openPortal} disabled={portalLoading}>
                  {portalLoading ? "loading..." : "Billing"}
                </button>
              </div>
            </>
          )}

          <StripePricingTableModal
            open={pricingOpen}
            onClose={() => setPricingOpen(false)}
          />

        </div>
      )}
    </div>
  );
}
